/**
 * The session of the account on screen (#1809, WS_DESIGN_1809 W5 to W7): balances, positions, open orders, fills and
 * order history, held above the routes for as long as that account stays on screen. One object per user and network, so
 * dropping it drops the data.
 *
 * Each HeldStream is one stream, and the stream's first frame is the snapshot (full state within about 150 ms of every
 * subscribe, STREAMS.md), so a page change or a reconnect costs no REST. REST is the fallback, paid from the one
 * background pacer: `fallbackMs` after the data stopped being fresh, then every `downPollMs` while that lasts.
 *
 * Fresh means a frame or a REST answer in the last `freshMs`, on streams that normally send a frame at least every 5 s.
 * It ends at once when the socket leaves `open`, and on an own order or fill (HeldStream.invalidate): the balance frame
 * follows those by 100 to 250 ms, and an answer that started before them says nothing about them. The order ticket does
 * not act while balances are not fresh.
 *
 * orderUpdates frames carry no user, so with two users' subscriptions on a socket nobody could tell whose frame it is.
 * Only holdOwnEvents subscribes it, for the connected wallet: a ghost's open orders follow the openOrders stream alone.
 *
 * Fills and order history do not fit HeldStream: they accumulate instead of being replaced, see sessionFills.ts and
 * sessionHistory.ts.
 */
import { isAbortError, type InfoClient } from '../hl/info';
import type { ClearinghouseState, FrontendOrder, SpotClearinghouseState, Subscription, SubscriptionData } from '../hl/types';
import { backgroundPacer, type WeightPacer } from '../hl/weightPacer';
import type { WsManager } from '../hl/ws';
import { applyOrderUpdates, sortOrdersDesc } from './orders/model';
import { orderEvents } from './orders/orderEvents';
import { FILLS_DOWNLOAD_DELAY_MS, SessionFills } from './sessionFills';
import { SessionHistory } from './sessionHistory';

/** How long data may go without a frame or a REST answer. Three missed refreshes of a stream that sends one every 5 s. */
export const FRESH_MS = 15_000;
/** Wait for a frame before reading over REST. A short reconnect costs nothing: the resubscribe brings the state. */
export const SESSION_FALLBACK_MS = 2_500;
/** REST refresh of balances while there is no stream. Inside FRESH_MS, so the ticket keeps working on REST alone. */
export const BALANCES_DOWN_POLL_MS = 10_000;
/** REST refresh of the rest while there is no stream. */
export const DOWN_POLL_MS = 30_000;

/** Main dex only (spec section 1). */
export const MAIN_DEX = '';

const SPOT_WEIGHT = 2;
const PERP_WEIGHT = 2;
const OPEN_ORDERS_WEIGHT = 20;

export interface HeldState<T> {
  data: T | undefined;
  /** True until the first frame or REST answer, or the first failed read. */
  isLoading: boolean;
  /** The first read failed and there is nothing to show. */
  error: Error | null;
  /** Where the current data came from last. */
  source: 'rest' | 'ws' | 'event' | null;
  /** The data may not be current: never act on it. */
  stale: boolean;
}

export interface SessionTiming {
  fallbackMs?: number;
  freshMs?: number;
  balancesDownPollMs?: number;
  downPollMs?: number;
  /** Before the fills and order history downloads. */
  fillsDelayMs?: number;
}

interface StreamDeps {
  ws: WsManager;
  pacer: WeightPacer;
  fallbackMs: number;
  freshMs: number;
}

interface StreamConfig<T, S extends Subscription> {
  sub: S;
  /** The state in a frame, or null to ignore the frame. */
  fromFrame: (data: SubscriptionData<S>) => T | null;
  read: (signal: AbortSignal) => Promise<T>;
  weight: number;
  downPollMs: number;
  /** True when `a` is strictly newer than `b`. */
  isNewer?: (a: T, b: T) => boolean;
}

export class HeldStream<T, S extends Subscription = Subscription> {
  private state: HeldState<T> = { data: undefined, isLoading: true, error: null, source: null, stale: true };
  private readonly listeners = new Set<() => void>();
  private running = false;
  private fresh = false;
  private offs: (() => void)[] = [];
  private pollDelay: number;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private freshTimer: ReturnType<typeof setTimeout> | null = null;
  private reading: AbortController | null = null;

  constructor(
    private readonly deps: StreamDeps,
    private readonly cfg: StreamConfig<T, S>,
  ) {
    this.pollDelay = deps.fallbackMs;
  }

  readonly getState = (): HeldState<T> => this.state;

  readonly subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  start(): void {
    if (this.running) return;
    this.running = true;
    const { ws } = this.deps;
    this.offs = [
      ws.subscribe(this.cfg.sub, (data) => this.onFrame(data)),
      ws.onStateChange((s) => {
        if (s !== 'open') this.setUnfresh();
        this.keepPolling();
      }),
    ];
    this.keepPolling();
  }

  stop(): void {
    this.running = false;
    for (const off of this.offs) off();
    this.offs = [];
    this.stopPollTimer();
    this.cancelRead();
    this.setUnfresh();
  }

  /** The held state may be out of date from now on: wait for a frame, then read. Drops a read already on its way. */
  invalidate(): void {
    this.setUnfresh();
    this.cancelRead();
    this.stopPollTimer();
    this.pollDelay = this.deps.fallbackMs;
    this.keepPolling();
  }

  /** Change what is held, unless nothing is yet: the first list will contain it. */
  applyEvent(next: (data: T) => T): void {
    if (this.state.data !== undefined) this.publish({ data: next(this.state.data), source: 'event' });
  }

  private onFrame(data: SubscriptionData<S>): void {
    const next = this.cfg.fromFrame(data);
    if (next === null) return;
    this.cancelRead();
    this.pollDelay = this.deps.fallbackMs;
    this.accept(next, 'ws');
  }

  private accept(next: T, source: 'rest' | 'ws'): void {
    const held = this.state.data;
    const keep = held !== undefined && this.cfg.isNewer?.(held, next) === true;
    this.fresh = true;
    if (this.freshTimer) clearTimeout(this.freshTimer);
    this.freshTimer = setTimeout(() => {
      this.freshTimer = null;
      this.setUnfresh();
      this.keepPolling();
    }, this.deps.freshMs);
    this.publish({ ...(keep ? {} : { data: next, source }), isLoading: false, error: null, stale: false });
    this.keepPolling();
  }

  private setUnfresh(): void {
    this.fresh = false;
    if (this.freshTimer) clearTimeout(this.freshTimer);
    this.freshTimer = null;
    if (!this.state.stale) this.publish({ stale: true });
  }

  /** Reads go on while the socket is down, even with fresh data, or the data would stop being fresh. A sleeping tab reads nothing. */
  private keepPolling(): void {
    if (!this.running) return;
    if (this.deps.ws.suspended) {
      this.stopPollTimer();
      this.cancelRead();
      this.pollDelay = this.deps.fallbackMs;
      return;
    }
    if (this.fresh && this.deps.ws.state === 'open') {
      this.stopPollTimer();
      return;
    }
    if (this.pollTimer === null && this.reading === null) {
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null;
        void this.read();
      }, this.pollDelay);
    }
  }

  private async read(): Promise<void> {
    const ac = new AbortController();
    this.reading = ac;
    this.pollDelay = this.cfg.downPollMs;
    try {
      await this.deps.pacer.take(this.cfg.weight, { signal: ac.signal });
      const value = await this.cfg.read(ac.signal);
      if (ac.signal.aborted) return;
      this.reading = null;
      this.accept(value, 'rest');
    } catch (e) {
      if (ac.signal.aborted || isAbortError(e)) return;
      this.reading = null;
      // A failed refresh leaves the list on screen; `stale` and the status dot already say what is going on.
      if (this.state.data === undefined) {
        this.publish({ isLoading: false, error: e instanceof Error ? e : new Error(String(e)) });
      }
      this.keepPolling();
    }
  }

  private cancelRead(): void {
    this.reading?.abort();
    this.reading = null;
  }

  private stopPollTimer(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private publish(patch: Partial<HeldState<T>>): void {
    this.state = { ...this.state, ...patch };
    for (const l of [...this.listeners]) l();
  }
}

type SpotSub = Extract<Subscription, { type: 'spotState' }>;
type PerpSub = Extract<Subscription, { type: 'clearinghouseState' }>;
type OrdersSub = Extract<Subscription, { type: 'openOrders' }>;

export class UserSession {
  readonly balances: HeldStream<SpotClearinghouseState, SpotSub>;
  readonly positions: HeldStream<ClearinghouseState, PerpSub>;
  readonly orders: HeldStream<FrontendOrder[], OrdersSub>;
  readonly fills: SessionFills;
  readonly history: SessionHistory;
  private readonly streams: readonly { start(): void; stop(): void }[];

  /** `user` in lower case. */
  constructor(
    private readonly ws: WsManager,
    info: InfoClient,
    readonly user: string,
    timing: SessionTiming = {},
  ) {
    const deps: StreamDeps = {
      ws,
      pacer: backgroundPacer(info),
      fallbackMs: timing.fallbackMs ?? SESSION_FALLBACK_MS,
      freshMs: timing.freshMs ?? FRESH_MS,
    };
    const downPollMs = timing.downPollMs ?? DOWN_POLL_MS;
    this.balances = new HeldStream<SpotClearinghouseState, SpotSub>(deps, {
      // Verified param name is ignorePortfolioMargin (STREAMS.md S2 Q1); false = the default view.
      sub: { type: 'spotState', user, ignorePortfolioMargin: false },
      fromFrame: (msg) => (msg.user?.toLowerCase() === user && msg.spotState ? msg.spotState : null),
      read: (signal) => info.spotClearinghouseState(user, { signal }),
      weight: SPOT_WEIGHT,
      downPollMs: timing.balancesDownPollMs ?? BALANCES_DOWN_POLL_MS,
    });
    this.positions = new HeldStream<ClearinghouseState, PerpSub>(deps, {
      sub: { type: 'clearinghouseState', user, dex: MAIN_DEX },
      fromFrame: (msg) =>
        msg.user?.toLowerCase() === user && (msg.dex ?? MAIN_DEX) === MAIN_DEX && msg.clearinghouseState
          ? msg.clearinghouseState
          : null,
      read: (signal) => info.clearinghouseState(user, MAIN_DEX, { signal }),
      weight: PERP_WEIGHT,
      downPollMs,
      isNewer: (a, b) => typeof a.time === 'number' && typeof b.time === 'number' && a.time > b.time,
    });
    this.orders = new HeldStream<FrontendOrder[], OrdersSub>(deps, {
      sub: { type: 'openOrders', user, dex: MAIN_DEX },
      fromFrame: (msg) => (Array.isArray(msg.orders) ? sortOrdersDesc([...msg.orders]) : null),
      read: async (signal) => sortOrdersDesc([...(await info.frontendOpenOrders(user, MAIN_DEX, { signal }))]),
      weight: OPEN_ORDERS_WEIGHT,
      downPollMs,
    });
    const held = {
      ws,
      info,
      pacer: deps.pacer,
      user,
      delayMs: timing.fillsDelayMs ?? FILLS_DOWNLOAD_DELAY_MS,
      fallbackMs: deps.fallbackMs,
      retryMs: downPollMs,
    };
    this.fills = new SessionFills(held);
    this.history = new SessionHistory(held);
    this.streams = [this.balances, this.positions, this.orders, this.fills, this.history];
  }

  start(): void {
    for (const s of this.streams) s.start();
  }

  stop(): void {
    for (const s of this.streams) s.stop();
  }

  /**
   * For the connected wallet's own session: order events reach the open orders at once and mark balances out of date.
   * Both streams are already held for the toasts (OrderToasts), so this adds listeners, not subscriptions.
   */
  holdOwnEvents(): () => void {
    const { user, ws } = this;
    const offUpdates = ws.subscribe({ type: 'orderUpdates', user }, (updates) => {
      if (!Array.isArray(updates)) return;
      orderEvents.emit(user, updates);
      this.orders.applyEvent((orders) => applyOrderUpdates(orders, updates));
      this.balances.invalidate();
    });
    const offFills = ws.subscribe({ type: 'userFills', user }, (msg) => {
      if (!msg.isSnapshot && msg.fills?.length) this.balances.invalidate();
    });
    return () => {
      offUpdates();
      offFills();
    };
  }

  /** The order was accepted. With a socket the events above say so; without one nothing would until the next poll. */
  afterOrder(): void {
    if (this.ws.state !== 'open') this.balances.invalidate();
  }
}
